import { useState } from 'react'
import { setKpiValue, type KpiCadence, type KpiMetric } from '../lib/kpiApi'
import { formatValue, targetLabel, valueClass } from '../lib/kpiFormat'
import { formatDate } from '../lib/grouping'
import KpiEntry from './KpiEntry'

interface Props {
  metric: KpiMetric
  token: string
  isAdmin: boolean
  onChanged: () => void
  /** Opens the admin edit form, pre-filled. */
  onEdit: (metric: KpiMetric) => void
  onArchiveToggle: (metric: KpiMetric) => void
  archiveBusy: boolean
}

/** "Week of Aug 24, 2026" for a weekly metric, just the date for a daily one. */
function periodLabel(cadence: KpiCadence, periodStart: string): string {
  const date = formatDate(periodStart)
  return cadence === 'daily' ? date : `Week of ${date}`
}

/**
 * One metric as a card — the My Portal view, where a person sees only their
 * own numbers and a big current value plus a history fold reads better than a
 * one-row grid. The Scorecard tab shows the same data as a grid (KpiGrid).
 */
export default function KpiCard({
  metric,
  token,
  isAdmin,
  onChanged,
  onEdit,
  onArchiveToggle,
  archiveBusy,
}: Props) {
  const [entryOpen, setEntryOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  // Assigned person, or an admin fixing a typo — the database enforces this
  // regardless of what the UI shows, but hiding the control for anyone else
  // keeps the card honest about who it belongs to.
  const canEdit = metric.mine || isAdmin
  const periodWord = metric.cadence === 'daily' ? "today's" : "this week's"

  const save = async (value: number) => {
    setBusy(true)
    setProblem(null)
    try {
      await setKpiValue(token, metric.metric_id, value)
      setEntryOpen(false)
      onChanged()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const target = targetLabel(metric)

  return (
    <article className={'card kpi-card' + (metric.mine ? ' kpi-card--mine' : '')}>
      <div className="card__head">
        <h3 className="card__title">{metric.name}</h3>
        {metric.archived && <span className="chip chip--low">Archived</span>}
      </div>

      <p className="card__due">
        <span className={metric.mine ? 'who__chip who__chip--me' : undefined}>
          {metric.mine ? 'You' : metric.employee_name}
        </span>
        {target && <>{' · Target '}{target}</>}
      </p>

      <div className="kpi__row">
        <span className={valueClass(metric.current_met)}>
          {metric.current_value != null ? formatValue(metric.current_value, metric.value_type) : 'Not entered'}
        </span>
      </div>

      {canEdit &&
        (entryOpen ? (
          <KpiEntry
            valueType={metric.value_type}
            initialValue={metric.current_value}
            placeholder={
              metric.value_type === 'percentage' ? `E.g. 85 (${periodWord} %)` : `${periodWord} number`
            }
            ariaLabel={`Enter ${periodWord} answer`}
            busy={busy}
            problem={problem}
            onCancel={() => {
              setProblem(null)
              setEntryOpen(false)
            }}
            onSave={(v) => void save(v)}
          />
        ) : (
          <button
            type="button"
            className="links__add"
            onClick={() => {
              setProblem(null)
              setEntryOpen(true)
            }}
          >
            {metric.current_value != null
              ? isAdmin && !metric.mine
                ? 'Correct this number'
                : `Update ${periodWord} number`
              : `Enter ${periodWord} number`}
          </button>
        ))}

      {metric.history.length > 0 && (
        <div className="fold kpi__history">
          <button
            type="button"
            className="fold__head"
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen((o) => !o)}
          >
            <span className="fold__caret" aria-hidden="true">
              {historyOpen ? '▾' : '▸'}
            </span>
            History
            <span className="group__count">{metric.history.length}</span>
          </button>

          {historyOpen && (
            <ul className="mini fold__body">
              {metric.history.map((h) => (
                <li key={h.period_start} className="mini__row">
                  <span className="mini__meta">
                    <span>{periodLabel(metric.cadence, h.period_start)}</span>
                    <span className={valueClass(h.met)}>{formatValue(h.value, metric.value_type)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {isAdmin && (
        <div className="card__actions">
          <button type="button" className="btn btn--quiet" onClick={() => onEdit(metric)}>
            Edit
          </button>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={archiveBusy}
            onClick={() => onArchiveToggle(metric)}
          >
            {metric.archived ? 'Restore' : 'Archive'}
          </button>
        </div>
      )}
    </article>
  )
}
